import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaUserShield } from 'react-icons/fa';
import { FaPlus } from 'react-icons/fa6';
import { MdDashboard, MdArrowBack, MdListAlt, MdViewKanban } from 'react-icons/md';
import CrmListPage from 'components/app/crm/shared/CrmListPage';
import RequestKanbanBoard from 'components/app/account-management/shared/RequestKanbanBoard';
import { columnDefs as baseColumns } from 'components/app/account-management/request-user-account/Column';
import RequestUserAccount from 'components/app/account-management/request-user-account/RequestUserAccount';
import { getRequestUserAccounts, exportData } from 'redux/request/userAccountSlice';
import { getAllDepartments } from 'redux/organization/departmentSlice';
import { getAllSites } from 'redux/organization/siteSlice';

const columns = baseColumns.map((c) => ({ ...c, sortable: false }));

const TABS = [
  { key: 'all',    label: 'All',    Icon: MdListAlt,    thunk: getRequestUserAccounts, dataKey: 'allData' },
  { key: 'kanban', label: 'Kanban', Icon: MdViewKanban, type: 'custom' },
];

const DATE_FILTERS = [
  { key: 'created_at', label: 'Created at', paramKeys: ['created_at_gteq', 'created_at_lteq'] },
];

// ticket_status enum: submitted=1, in_progress=2, rejected=3, completed=4.
const KANBAN_COLUMNS = [
  { key: 'submitted',   label: 'Submitted',   color: '#64748b', params: { ticket_status_eq: 1 } },
  { key: 'in_progress', label: 'In progress', color: '#f59e0b', params: { ticket_status_eq: 2 } },
  { key: 'rejected',    label: 'Rejected',    color: '#ef4444', params: { ticket_status_eq: 3 } },
  { key: 'completed',   label: 'Completed',   color: '#10b981', params: { ticket_status_eq: 4 } },
];

const RequestUserAccountPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: departmentData = [] } = useSelector((s) => s.organization_department.allData);
  const { data: siteData = [] }       = useSelector((s) => s.organization_site.allData);

  const [showDashboard, setShowDashboard] = useState(false);

  useEffect(() => {
    dispatch(getAllDepartments());
    dispatch(getAllSites());
  }, [dispatch]);

  // Departments grouped by their site office (DepartmentSerializer → site_name).
  const departmentGroups = useMemo(() => {
    const map = {};
    departmentData.forEach((d) => {
      const site = d.site_name || 'No site';
      if (!map[site]) map[site] = { key: site, label: site, items: [] };
      map[site].items.push({ value: d.id, label: d.name });
    });
    return Object.values(map);
  }, [departmentData]);

  const multiSelects = useMemo(() => [
    { key: 'department', paramKey: 'department_id_in', placeholder: 'All departments', groups: departmentGroups, width: 280 },
    { key: 'site',       paramKey: 'site_id_in',       placeholder: 'All sites',       options: siteData.map((s) => ({ value: s.id, label: s.name })) },
  ], [departmentGroups, siteData]);

  const headerExtra = (
    <>
      <button type="button" onClick={() => setShowDashboard(true)}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer">
        <MdDashboard size={15} /> Dashboard
      </button>
      <button type="button" onClick={() => navigate('/request/user-accounts/new')}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-primary-600 tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 tw-text-sm tw-font-medium tw-cursor-pointer">
        <FaPlus size={11} /> New Request
      </button>
    </>
  );

  if (showDashboard) {
    return (
      <div className="tw-p-4">
        <button type="button" onClick={() => setShowDashboard(false)}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-mb-3 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer">
          <MdArrowBack size={15} /> Back to requests
        </button>
        <RequestUserAccount />
      </div>
    );
  }

  return (
    <CrmListPage
      title="User Account Requests"
      subtitle="Manage and track all user account requests."
      Icon={FaUserShield}
      reducerSelector={(s) => s.request_user_account}
      tabs={TABS}
      columns={columns}
      searchParamKey="name_or_first_name_or_last_name_cont"
      searchPlaceholder="Search by name..."
      dateFilters={DATE_FILTERS}
      multiSelects={multiSelects}
      exportThunk={exportData}
      headerExtra={headerExtra}
      renderCustomTab={({ listParams }) => (
        <RequestKanbanBoard
          columns={KANBAN_COLUMNS}
          listParams={listParams}
          thunk={getRequestUserAccounts}
          detailPath="/request/user-accounts"
          getTitle={(r) => r.full_of_name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.name}
          getSubtitle={(r) => r.title}
        />
      )}
    />
  );
};

export default RequestUserAccountPage;
